// NOTE: This is part of the lower level of the New Computation Model, and
// should be kept isolated from non-trivial Glide dependencies.  In
// particular, it shouldn't have to know anything about Glide table/column
// types/schemas.

import { assert } from "@glideapps/ts-necessities";
import toPairs from "lodash/toPairs";
import type { DocumentData, RowIndex } from "../common/Database";
import { isBaseRowIndex } from "../common/firebase-function-types";
import { convertGlideDateTime } from "../common/glide-date-time";
import type { GroundValue, Row, Table } from "../common/computation-model/data";
import type { IncomingSlot, Namespace } from "../common/computation-model/types";
import type { TableKeeper } from "../common/computation-model/table-keeper";

// Dirt:
// * pushes dirt on all cells that are modified.
export class SimpleTableKeeper implements TableKeeper {
    private _ns: Namespace | undefined;
    private readonly _table = new Map<string, Row>();
    public readonly symbolicRepresentation = "simple-table-keeper";

    // Used for debugging
    public get table(): Table {
        return this._table;
    }

    public getSlots(): readonly IncomingSlot[] {
        return [];
    }

    public recompute(): GroundValue {
        return this._table;
    }

    private pushDirtForRow(rowID: string): void {
        this._ns?.pushDirt(this, { kind: "row", rowID, columns: true });
    }

    public addRow(row: Row): void {
        assert(!this._table.has(row.$rowID));

        row = { ...row };
        this._table.set(row.$rowID, row);
        this.pushDirtForRow(row.$rowID);
    }

    public updateRowWithPartialData(rowIndex: RowIndex, updates: DocumentData): void {
        assert(!isBaseRowIndex(rowIndex));
        assert(rowIndex.keyColumnName === "$rowID");

        const rowID = rowIndex.keyColumnValue;
        assert(typeof rowID === "string");

        const row = this._table.get(rowID);
        if (row === undefined) return;

        const columns = new Set<string>();
        for (const [k, v] of toPairs(updates)) {
            row[k] = convertGlideDateTime(v);
            columns.add(k);
        }

        this._ns?.pushDirt(this, { kind: "row", rowID, columns });
    }

    public setColumnInRow(rowID: string, columnName: string, value: GroundValue): void {
        this.updateRowWithPartialData({ keyColumnName: "$rowID", keyColumnValue: rowID }, { [columnName]: value });
    }

    public addInvisibleRow(row: Row): void {
        assert(!row.$isVisible);

        this.addRow(row);
    }

    public deleteInvisibleRow(rowID: string): void {
        const row = this._table.get(rowID);
        if (row === undefined) return;

        assert(!row.$isVisible);
        this._table.delete(rowID);
        this.pushDirtForRow(rowID);
    }

    public setDirty(): void {
        return;
    }

    public get isDirty(): boolean {
        return false;
    }

    public connect(ns: Namespace): void {
        this._ns = ns;
    }
}
